import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import * as inviteService from '../../../services/inviteService';
import { ResponsiveGrid, ResponsiveContainer, ResponsiveButton } from '../../common/ResponsiveLayout';
import InviteList from './InviteList';
import InviteModal from './InviteModal';
import InviteDetailsModal from './InviteDetailsModal';
import toast from 'react-hot-toast';

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * InviteManagement Component
 * Main container for invite management functionality
 */
const InviteManagement = () => {
  const { user, userRole, loading: authLoading } = useAuth();
  const [invites, setInvites] = useState([]);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);

  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ 
    total: 0, 
    pending: 0, 
    claimed: 0, 
    expired: 0, 
    revoked: 0 
  });

  // Debounce search to avoid too many API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchInvites = useCallback(async (options = {}) => {
    if (!user || !userRole) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const searchOptions = {
        page: currentPage,
        pageSize,
        search: debouncedSearchTerm,
        statusFilter,
        sortBy,
        sortOrder,
        ...options
      };

      const {
        data: fetchedInvites,
        error: fetchError,
        count,
        totalPages: pages
      } = await inviteService.getInvites(user.id, userRole, searchOptions);

      if (fetchError) {
        throw new Error(fetchError);
      }
      
      setInvites(fetchedInvites || []);
      setTotalCount(count || 0);
      setTotalPages(pages || 0);
    } catch (err) {
      console.error('Failed to fetch invites:', err);
      setError(err.message || 'Failed to fetch invites');
    } finally {
      setLoading(false);
    }
  }, [user, userRole, currentPage, pageSize, debouncedSearchTerm, statusFilter, sortBy, sortOrder]);

  const fetchStatusCounts = useCallback(async () => {
    if (!user || !userRole) return;
    
    try {
      const { data: counts, error } = await inviteService.getInviteStatusCounts(user.id, userRole);
      
      if (error) {
        console.error('Failed to fetch status counts:', error);
      } else {
        setStatusCounts(counts);
      }
    } catch (err) {
      console.error('Failed to fetch status counts:', err);
    }
  }, [user, userRole]);

  // Fetch invites and status counts when dependencies change
  useEffect(() => {
    if (!authLoading && user && userRole) {
      fetchInvites();
      fetchStatusCounts();
    } else if (!authLoading && !user) {
      setLoading(false);
      setError("You must be logged in to view this page.");
    } else if (!authLoading && user && !userRole) {
      setLoading(false);
      setError("Could not determine user role. Please contact support.");
    }
  }, [authLoading, user, userRole, fetchInvites, fetchStatusCounts]);

  // Reset to first page when search or filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, statusFilter]);

  // Search and filter handlers
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  const handleSortOrderChange = (value) => {
    setSortOrder(value);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Invite management handlers
  const handleCreateInvite = async (inviteData) => {
    setOperationLoading(true);
    setError(null);
    
    try {
      const { data, error } = await inviteService.createInvite(
        inviteData.email,
        inviteData.preFilledData,
        inviteData.expiresInDays
      );

      if (error) {
        throw new Error(error);
      }

      toast.success('Invite sent successfully!');
      await fetchInvites(); // Refresh the list
      await fetchStatusCounts(); // Refresh the counts
      setIsInviteModalOpen(false); // Close modal on success
      
      // Show invite link for easy sharing
      if (data?.invite_token) {
        const inviteUrl = inviteService.generateInviteUrl(data.invite_token);
        toast.success(
          `Invite created! Link: ${inviteUrl}`,
          { duration: 10000 }
        );
      }
    } catch (err) {
      console.error('Failed to create invite:', err);
      setError(err.message || 'Failed to create invite');
      toast.error(err.message || 'Failed to create invite');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleResendInvite = async (invite) => {
    if (!window.confirm(`Are you sure you want to resend the invite to ${invite.email}? This will create a new invite link and revoke the old one.`)) {
      return;
    }

    setOperationLoading(true);
    setError(null);
    
    try {
      const { data, error } = await inviteService.resendInvite(invite.id);

      if (error) {
        throw new Error(error);
      }

      toast.success('Invite resent successfully!');
      await fetchInvites(); // Refresh the list
      await fetchStatusCounts(); // Refresh the counts
      setIsDetailsModalOpen(false); // Close details modal
      
      // Show new invite link
      if (data?.invite_token) {
        const inviteUrl = inviteService.generateInviteUrl(data.invite_token);
        toast.success(
          `New invite link: ${inviteUrl}`,
          { duration: 10000 }
        );
      }
    } catch (err) {
      console.error('Failed to resend invite:', err);
      setError(err.message || 'Failed to resend invite');
      toast.error(err.message || 'Failed to resend invite');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleRevokeInvite = async (invite) => {
    const reason = window.prompt('Please provide a reason for revoking this invite (optional):');
    if (reason === null) return; // User cancelled

    setOperationLoading(true);
    setError(null);
    
    try {
      const { error } = await inviteService.revokeInvite(invite.id, reason || undefined);

      if (error) {
        throw new Error(error);
      }

      toast.success('Invite revoked successfully!');
      await fetchInvites(); // Refresh the list
      await fetchStatusCounts(); // Refresh the counts
      setIsDetailsModalOpen(false); // Close details modal
    } catch (err) {
      console.error('Failed to revoke invite:', err);
      setError(err.message || 'Failed to revoke invite');
      toast.error(err.message || 'Failed to revoke invite');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleViewInvite = (invite) => {
    setSelectedInvite(invite);
    setIsDetailsModalOpen(true);
    setError(null); // Clear any existing errors
  };

  const handleCloseDetailsModal = () => {
    setSelectedInvite(null);
    setIsDetailsModalOpen(false);
    setError(null); // Clear any existing errors
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setError(null); // Clear any existing errors
  };

  // Cleanup expired and revoked invites
  const handleCleanupExpired = async () => {
    if (!window.confirm('This will remove all expired and revoked invites from the system. This action cannot be undone. Continue?')) {
      return;
    }

    setOperationLoading(true);
    
    try {
      const { data: deletedCount, error } = await inviteService.cleanupOldInvites();

      if (error) {
        throw new Error(error);
      }

      toast.success(`${deletedCount} old invites removed`);
      await fetchInvites(); // Refresh the list
      await fetchStatusCounts(); // Refresh the counts
    } catch (err) {
      console.error('Failed to cleanup old invites:', err);
      toast.error(err.message || 'Failed to cleanup old invites');
    } finally {
      setOperationLoading(false);
    }
  };

  // Check if user has admin privileges
  const hasAdminAccess = userRole === 'SUPER_ADMIN';
  const hasCoachAccess = userRole === 'COACH';
  const canManageInvites = hasAdminAccess || hasCoachAccess;

  if (authLoading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          You must be logged in to view this page.
        </div>
      </div>
    );
  }

  if (!canManageInvites) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Access denied. You must be an admin or coach to access invite management.
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invite Management</h1>
          <p className="text-gray-600 mt-1">
            {hasAdminAccess ? 'Manage all user invites' : 'Manage your client invites'}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          {hasAdminAccess && (
            <ResponsiveButton
              onClick={handleCleanupExpired}
              variant="secondary"
              disabled={operationLoading}
              size="sm"
              className="text-xs px-4 py-2"
            >
              Cleanup Old Invites
            </ResponsiveButton>
          )}
          
          <ResponsiveButton
            onClick={() => {
              setIsInviteModalOpen(true);
              setError(null);
            }}
            variant="primary"
            disabled={operationLoading}
            size={{ xs: 'md', lg: 'md' }}
            className="w-full sm:w-auto"
          >
            Send New Invite
          </ResponsiveButton>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex">
            <div className="flex-1">
              <strong>Error:</strong> {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-700 hover:text-red-900 ml-2 font-bold text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <ResponsiveGrid
        columns={{ xs: 5, sm: 5, md: 5 }}
        gap={2}
        className="mb-4"
      >
        <StatsCard
          value={statusCounts.total}
          label="Total Invites"
          color="gray"
        />
        <StatsCard
          value={statusCounts.pending}
          label="Pending"
          color="yellow"
        />
        <StatsCard
          value={statusCounts.claimed}
          label="Claimed"
          color="green"
        />
        <StatsCard
          value={statusCounts.expired}
          label="Expired"
          color="orange"
        />
        <StatsCard
          value={statusCounts.revoked}
          label="Revoked"
          color="red"
        />
      </ResponsiveGrid>

      {/* Invite List */}
      <InviteList
        invites={invites}
        loading={loading}
        onView={handleViewInvite}
        onResend={handleResendInvite}
        onRevoke={handleRevokeInvite}
        // Search and filter props
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        sortOrder={sortOrder}
        onSortOrderChange={handleSortOrderChange}
        onClearFilters={handleClearFilters}
        // Pagination props
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Invite Modal */}
      <InviteModal
        isOpen={isInviteModalOpen}
        onSave={handleCreateInvite}
        onCancel={handleCloseInviteModal}
        loading={operationLoading}
      />

      {/* Invite Details Modal */}
      <InviteDetailsModal
        isOpen={isDetailsModalOpen}
        invite={selectedInvite}
        onClose={handleCloseDetailsModal}
        onResend={handleResendInvite}
        onRevoke={handleRevokeInvite}
        loading={operationLoading}
      />
    </ResponsiveContainer>
  );
};

// Stats Card Component
const StatsCard = ({ value, label, color = 'gray' }) => {
  const colorClasses = {
    gray: 'text-gray-900',
    blue: 'text-blue-600',
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    orange: 'text-orange-600',
    red: 'text-red-600',
    purple: 'text-purple-600'
  };

  return (
    <div className="bg-white shadow-sm rounded-lg p-3 border border-gray-200">
      <div className={`text-xl font-bold ${colorClasses[color]}`}>
        {value}
      </div>
      <div className="text-xs text-gray-600 mt-1">
        {label}
      </div>
    </div>
  );
};

export default InviteManagement;